import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";

type PaymentMethod = "CASH" | "SCAN";

type Expense = {
  id: number;
  expense_date: string;
  item_name: string;
  amount: number;
  payment_method: PaymentMethod;
  note: string;
};

type ExpensesResponse = {
  items?: unknown;
};

type ExpenseItemOption = {
  id: number;
  name: string;
};

type ExpenseItemOptionsResponse = {
  items?: unknown;
  error?: string;
};

type ExpenseForm = {
  expense_date: string;
  item_name: string;
  amount: string;
  payment_method: PaymentMethod;
  note: string;
};

const EXPENSES_API = apiUrl("/expenses");
const EXPENSE_ITEM_OPTIONS_API = apiUrl("/expense-item-options");

const INITIAL_FORM: ExpenseForm = {
  expense_date: "",
  item_name: "",
  amount: "",
  payment_method: "CASH",
  note: "",
};

function isPaymentMethod(value: unknown): value is PaymentMethod {
  return value === "CASH" || value === "SCAN";
}

function toExpenseItemOption(row: unknown): ExpenseItemOption | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  if (typeof data.name !== "string" || !data.name.trim()) return null;

  return {
    id,
    name: data.name.trim(),
  };
}

function toExpense(row: unknown): Expense | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id);
  const amount = Number(data.amount);
  const paymentMethod = data.payment_method;

  if (!Number.isInteger(id) || id <= 0) return null;
  if (typeof data.expense_date !== "string") return null;
  if (typeof data.item_name !== "string") return null;
  if (!Number.isFinite(amount)) return null;
  if (!isPaymentMethod(paymentMethod)) return null;

  return {
    id,
    expense_date: data.expense_date,
    item_name: data.item_name,
    amount,
    payment_method: paymentMethod,
    note: typeof data.note === "string" ? data.note : "",
  };
}

function paymentLabel(value: PaymentMethod): string {
  return value === "CASH" ? "เงินสด" : "แสกน";
}

function todayIso(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDisplayDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

function parseDisplayDate(value: string): string | null {
  const normalized = value.trim();
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(normalized);
  if (!match) return null;
  const [, day, month, year] = match;
  const iso = `${year}-${month}-${day}`;
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  if (date.getFullYear() !== Number(year)) return null;
  if (date.getMonth() + 1 !== Number(month)) return null;
  if (date.getDate() !== Number(day)) return null;
  return iso;
}

type DateInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
};

// PMN-CRITICAL(/ExpensesPage): UI displays dd/mm/yyyy, but all expense API payloads must stay ISO yyyy-mm-dd.
function DateInput({
  value,
  onChange,
  placeholder = "dd/mm/yyyy",
  className = "form-control",
}: DateInputProps): JSX.Element {
  const [displayValue, setDisplayValue] = useState(() => formatDisplayDate(value));

  useEffect(() => {
    setDisplayValue(formatDisplayDate(value));
  }, [value]);

  return (
    <input
      type="text"
      inputMode="numeric"
      className={className}
      placeholder={placeholder}
      value={displayValue}
      onChange={(e) => {
        const next = e.target.value;
        setDisplayValue(next);
        if (!next.trim()) {
          onChange("");
          return;
        }
        const parsed = parseDisplayDate(next);
        if (parsed) {
          onChange(parsed);
        }
      }}
      onBlur={() => {
        if (!displayValue.trim()) {
          setDisplayValue("");
          return;
        }
        const parsed = parseDisplayDate(displayValue);
        setDisplayValue(parsed ? formatDisplayDate(parsed) : formatDisplayDate(value));
      }}
    />
  );
}

export default function ExpensesPage(): JSX.Element {
  const [items, setItems] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");
  const [itemOptions, setItemOptions] = useState<ExpenseItemOption[]>([]);
  const [itemOptionsLoading, setItemOptionsLoading] = useState(false);
  const [newItemOption, setNewItemOption] = useState("");
  const [addingItemOption, setAddingItemOption] = useState(false);
  const [showAddItemOption, setShowAddItemOption] = useState(false);

  const [form, setForm] = useState<ExpenseForm>(() => ({
    ...INITIAL_FORM,
    expense_date: todayIso(),
  }));

  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    payment_method: "",
    q: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    date_from: "",
    date_to: "",
    payment_method: "",
    q: "",
  });

  const [editing, setEditing] = useState<Expense | null>(null);
  const [editForm, setEditForm] = useState<ExpenseForm>(INITIAL_FORM);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [summarySelected, setSummarySelected] = useState<string[]>([]);
  const [showMonthlyBreakdown, setShowMonthlyBreakdown] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const toggleExpandMonth = (month: string) =>
    setExpandedMonths(prev => {
      const next = new Set(prev);
      next.has(month) ? next.delete(month) : next.add(month);
      return next;
    });

  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + item.amount, 0),
    [items]
  );
  const itemSummaryRows = useMemo(() => {
    const map = new Map<string, number>();
    for (const it of items) {
      map.set(it.item_name, (map.get(it.item_name) ?? 0) + it.amount);
    }
    return Array.from(map.entries())
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
  }, [items]);
  const summaryTotal = useMemo(
    () => items
      .filter((it) => summarySelected.includes(it.item_name))
      .reduce((s, it) => s + it.amount, 0),
    [items, summarySelected]
  );
  const toggleSummaryItem = (name: string) =>
    setSummarySelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );

  type MonthRow = { month: string; label: string; total: number; cats: { name: string; total: number }[] };
  const monthlyRows = useMemo<MonthRow[]>(() => {
    if (!summarySelected.length) return [];
    const monthMap = new Map<string, Map<string, number>>();
    for (const it of items) {
      if (!summarySelected.includes(it.item_name)) continue;
      const m = it.expense_date.slice(0, 7);
      if (!monthMap.has(m)) monthMap.set(m, new Map());
      monthMap.get(m)!.set(it.item_name, (monthMap.get(m)!.get(it.item_name) ?? 0) + it.amount);
    }
    return Array.from(monthMap.entries())
      .map(([month, catMap]) => {
        const cats = Array.from(catMap.entries())
          .map(([name, total]) => ({ name, total }))
          .sort((a, b) => b.total - a.total);
        const total = cats.reduce((s, c) => s + c.total, 0);
        const [y, mo] = month.split("-");
        const label = new Date(Number(y), Number(mo) - 1, 1)
          .toLocaleDateString("th-TH", { year: "numeric", month: "short" });
        return { month, label, total, cats };
      })
      .sort((a, b) => b.month.localeCompare(a.month));
  }, [items, summarySelected]);
  const createItemOptions = useMemo(() => {
    const selected = form.item_name.trim();
    if (!selected) return itemOptions;
    if (itemOptions.some((option) => option.name === selected)) return itemOptions;
    return [{ id: -1, name: selected }, ...itemOptions];
  }, [form.item_name, itemOptions]);
  const editItemOptions = useMemo(() => {
    const selected = editForm.item_name.trim();
    if (!selected) return itemOptions;
    if (itemOptions.some((option) => option.name === selected)) return itemOptions;
    return [{ id: -1, name: selected }, ...itemOptions];
  }, [editForm.item_name, itemOptions]);
  const allSelected =
    items.length > 0 && items.every((item) => selectedIds.includes(item.id));
  const rowToneById = useMemo(() => {
    const toneMap = new Map<number, "expense-row-soft" | "expense-row-muted">();
    let lastDate = "";
    let toneIndex = -1;

    for (const item of items) {
      if (item.expense_date !== lastDate) {
        lastDate = item.expense_date;
        toneIndex += 1;
      }
      toneMap.set(
        item.id,
        toneIndex % 2 === 0 ? "expense-row-soft" : "expense-row-muted"
      );
    }

    return toneMap;
  }, [items]);

  async function fetchExpenseItemOptions(): Promise<void> {
    try {
      setItemOptionsLoading(true);
      const resp = await fetch(EXPENSE_ITEM_OPTIONS_API);
      const data = (await resp.json().catch(() => ({}))) as ExpenseItemOptionsResponse;
      if (!resp.ok) {
        throw new Error(data.error ?? "failed_to_fetch_expense_item_options");
      }

      const list = Array.isArray(data.items)
        ? data.items
            .map(toExpenseItemOption)
            .filter((item): item is ExpenseItemOption => item !== null)
        : [];

      setItemOptions(list);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "failed_to_fetch_expense_item_options"
      );
    } finally {
      setItemOptionsLoading(false);
    }
  }

  async function fetchExpenses(currentFilters = appliedFilters): Promise<void> {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      if (currentFilters.date_from) params.set("date_from", currentFilters.date_from);
      if (currentFilters.date_to) params.set("date_to", currentFilters.date_to);
      if (currentFilters.payment_method) {
        params.set("payment_method", currentFilters.payment_method);
      }
      if (currentFilters.q.trim()) params.set("q", currentFilters.q.trim());

      const url = params.toString() ? `${EXPENSES_API}?${params.toString()}` : EXPENSES_API;
      const resp = await fetch(url);
      const data = (await resp.json().catch(() => ({}))) as ExpensesResponse & {
        error?: string;
      };

      if (!resp.ok) {
        throw new Error(data.error ?? "failed_to_fetch_expenses");
      }

      const list = Array.isArray(data.items)
        ? data.items.map(toExpense).filter((item): item is Expense => item !== null)
        : [];

      setItems(list);
      setSelectedIds((prev) => prev.filter((id) => list.some((item) => item.id === id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_fetch_expenses");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchExpenses(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  useEffect(() => {
    void fetchExpenseItemOptions();
  }, []);

  function resetCreateForm(nextExpenseDate?: string, nextItemName?: string): void {
    setForm({
      ...INITIAL_FORM,
      expense_date: nextExpenseDate ?? todayIso(),
      item_name: nextItemName ?? "",
    });
  }

  async function handleCreateItemOption(): Promise<void> {
    const name = newItemOption.trim();
    if (!name) {
      setError("กรุณากรอกชื่อรายการใหม่");
      return;
    }

    setAddingItemOption(true);
    setError("");
    setSuccessMsg("");

    try {
      const resp = await fetch(EXPENSE_ITEM_OPTIONS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error ?? "failed_to_create_expense_item_option");
      }

      setNewItemOption("");
      setForm((prev) => ({ ...prev, item_name: name }));
      if (editing) {
        setEditForm((prev) => ({ ...prev, item_name: name }));
      }
      setSuccessMsg("เพิ่มตัวเลือกของรายการเรียบร้อย");
      await fetchExpenseItemOptions();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "failed_to_create_expense_item_option"
      );
    } finally {
      setAddingItemOption(false);
    }
  }

  function openEdit(expense: Expense): void {
    setEditing(expense);
    setEditForm({
      expense_date: expense.expense_date,
      item_name: expense.item_name,
      amount: String(expense.amount),
      payment_method: expense.payment_method,
      note: expense.note,
    });
  }

  function closeEdit(): void {
    setEditing(null);
    setEditForm(INITIAL_FORM);
  }

  // PMN-CRITICAL(/ExpensesPage): Create/update validation mirrors backend; payment_method contract is only CASH or SCAN.
  async function handleCreate(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccessMsg("");

    try {
      const amount = Number(form.amount);
      if (!form.expense_date) throw new Error("กรุณาเลือกวันที่");
      if (!form.item_name.trim()) throw new Error("กรุณากรอกรายการ");
      if (!Number.isFinite(amount) || amount <= 0) throw new Error("กรุณากรอกราคาให้ถูกต้อง");

      const resp = await fetch(EXPENSES_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          expense_date: form.expense_date,
          item_name: form.item_name.trim(),
          amount,
          payment_method: form.payment_method,
          note: form.note.trim(),
        }),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error ?? "failed_to_create_expense");
      }

      setSuccessMsg("บันทึกรายจ่ายเรียบร้อย");
      resetCreateForm(form.expense_date, form.item_name);
      await fetchExpenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_create_expense");
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(): Promise<void> {
    if (!editing) return;

    setSaving(true);
    setError("");
    setSuccessMsg("");

    try {
      const amount = Number(editForm.amount);
      if (!editForm.expense_date) throw new Error("กรุณาเลือกวันที่");
      if (!editForm.item_name.trim()) throw new Error("กรุณากรอกรายการ");
      if (!Number.isFinite(amount) || amount <= 0) throw new Error("กรุณากรอกราคาให้ถูกต้อง");

      const resp = await fetch(apiUrl(`/expenses/${editing.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          expense_date: editForm.expense_date,
          item_name: editForm.item_name.trim(),
          amount,
          payment_method: editForm.payment_method,
          note: editForm.note.trim(),
        }),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error ?? "failed_to_update_expense");
      }

      setSuccessMsg("แก้ไขรายจ่ายเรียบร้อย");
      closeEdit();
      await fetchExpenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_update_expense");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(expense: Expense): Promise<void> {
    const ok = window.confirm(
      `ยืนยันลบรายการ "${expense.item_name}" วันที่ ${expense.expense_date} จำนวน ${expense.amount.toLocaleString(
        "th-TH",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )} บาท ?`
    );
    if (!ok) return;

    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      const resp = await fetch(apiUrl(`/expenses/${expense.id}`), {
        method: "DELETE",
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error ?? "failed_to_delete_expense");
      }

      setSuccessMsg("ลบรายจ่ายเรียบร้อย");
      await fetchExpenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_delete_expense");
    } finally {
      setLoading(false);
    }
  }

  function toggleSelected(id: number): void {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((currentId) => currentId !== id) : [...prev, id]
    );
  }

  function toggleSelectAll(): void {
    setSelectedIds((prev) => {
      if (items.length > 0 && items.every((item) => prev.includes(item.id))) {
        return [];
      }
      return items.map((item) => item.id);
    });
  }

  // PMN-CRITICAL(/ExpensesPage): Bulk delete is not atomic; requests run one by one and can partially succeed before an error.
  async function handleDeleteSelected(): Promise<void> {
    if (selectedIds.length === 0) return;

    const ok = window.confirm(`ยืนยันลบรายการที่เลือก ${selectedIds.length} รายการ ?`);
    if (!ok) return;

    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      for (const id of selectedIds) {
        const resp = await fetch(apiUrl(`/expenses/${id}`), {
          method: "DELETE",
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.error ?? "failed_to_delete_expense");
        }
      }

      setSelectedIds([]);
      setSuccessMsg(`ลบรายการเรียบร้อย ${selectedIds.length} รายการ`);
      await fetchExpenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_delete_expense");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-vh-100 bg-light">
      <div className="container py-4 py-md-5">
        <div className="row g-4">
          <div className="col-12 col-xl-4">
            <div className="card shadow-sm border-0 rounded-4">
              <div className="card-body p-4">
                <h4 className="mb-3 text-primary">บันทึกรายจ่าย</h4>
                <form className="d-grid gap-3" onSubmit={(e) => void handleCreate(e)}>
                  <div>
                    <label className="form-label">วันที่</label>
                    <DateInput
                      value={form.expense_date}
                      onChange={(next) =>
                        setForm((prev) => ({ ...prev, expense_date: next }))
                      }
                    />
                  </div>

                  <div>
                    <label className="form-label">รายการ</label>
                    <div className="d-flex align-items-center gap-2">
                    <select
                      className="form-select"
                      value={form.item_name}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, item_name: e.target.value }))
                      }
                      disabled={itemOptionsLoading}
                    >
                      <option value="">เลือกรายการ</option>
                      {createItemOptions.map((option) => (
                        <option key={option.id} value={option.name}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn d-inline-flex align-items-center justify-content-center border rounded-circle bg-light expense-option-toggle flex-shrink-0"
                      onClick={() => setShowAddItemOption((prev) => !prev)}
                      aria-expanded={showAddItemOption}
                      aria-label={
                        showAddItemOption
                          ? "เธเนเธญเธเธเธญเธฃเนเธกเน€เธเธดเนเธกเธฃเธฒเธขเธเธฒเธฃเนเธ dropdown"
                          : "เนเธชเธ”เธเธเธญเธฃเนเธกเน€เธเธดเนเธกเธฃเธฒเธขเธเธฒเธฃเนเธ dropdown"
                      }
                    >
                      <span className="fw-semibold text-primary">เน€เธเธดเนเธกเธฃเธฒเธขเธเธฒเธฃเนเธ dropdown</span>
                      <span className="expense-option-toggle__icon" aria-hidden="true">
                        {showAddItemOption ? "-" : "+"}
                      </span>
                    </button>
                    </div>
                    <div className="form-text">
                      {itemOptionsLoading
                        ? "กำลังโหลดตัวเลือก..."
                        : "เลือกรายการจาก dropdown หรือเพิ่มรายการใหม่ด้านล่าง"}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn d-none align-items-center justify-content-center border rounded-circle bg-light expense-option-toggle"
                    onClick={() => setShowAddItemOption((prev) => !prev)}
                    aria-expanded={showAddItemOption}
                    aria-label={
                      showAddItemOption
                        ? "ซ่อนฟอร์มเพิ่มรายการใน dropdown"
                        : "แสดงฟอร์มเพิ่มรายการใน dropdown"
                    }
                  >
                    <span className="fw-semibold text-primary">เพิ่มรายการใน dropdown</span>
                    <span className="expense-option-toggle__icon" aria-hidden="true">
                      {showAddItemOption ? "-" : "+"}
                    </span>
                  </button>

                  <div className={`expense-option-panel ${showAddItemOption ? "is-open" : ""}`}>
                    <div className="border rounded-3 p-3 bg-light">
                    <label className="form-label">เพิ่มรายการใหม่ใน dropdown</label>
                    <div className="d-flex flex-column flex-md-row gap-2">
                      <input
                        type="text"
                        className="form-control"
                        value={newItemOption}
                        onChange={(e) => setNewItemOption(e.target.value)}
                        placeholder="เช่น ค่าอินเทอร์เน็ต"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => void handleCreateItemOption()}
                        disabled={addingItemOption}
                      >
                        {addingItemOption ? "กำลังเพิ่ม..." : "เพิ่มรายการ"}
                      </button>
                    </div>
                  </div>
                  </div>

                  <div>
                    <label className="form-label">ราคา</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      step="0.01"
                      value={form.amount}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, amount: e.target.value }))
                      }
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="form-label">วิธีจ่าย</label>
                    <select
                      className="form-select"
                      value={form.payment_method}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          payment_method: e.target.value as PaymentMethod,
                        }))
                      }
                    >
                      <option value="CASH">เงินสด</option>
                      <option value="SCAN">แสกน</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">โน้ต</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={form.note}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, note: e.target.value }))
                      }
                      placeholder="รายละเอียดเพิ่มเติม"
                    />
                  </div>

                  <div className="d-grid gap-2">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? "กำลังบันทึก..." : "บันทึกรายจ่าย"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => resetCreateForm()}
                      disabled={saving}
                    >
                      ล้างฟอร์ม
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-8">
            <div className="card shadow-sm border-0 rounded-4">
              <div className="card-body p-4">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
                  <div>
                    <h4 className="mb-1 text-primary">จัดการค่าใช้จ่าย</h4>
                    <div className="small text-muted">
                      ทั้งหมด {items.length} รายการ รวม{" "}
                      {totalAmount.toLocaleString("th-TH", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      บาท
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => void fetchExpenses()}
                      disabled={loading}
                    >
                      รีเฟรช
                    </button>
                    <a href="/" className="btn btn-outline-secondary text-decoration-none">
                      ← กลับหน้าเมนู
                    </a>
                  </div>
                </div>

                <div className="row g-2 align-items-end mb-3">
                  <div className="col-12 col-md-3">
                    <label className="form-label">จากวันที่</label>
                    <DateInput
                      value={filters.date_from}
                      onChange={(next) =>
                        setFilters((prev) => ({ ...prev, date_from: next }))
                      }
                    />
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">ถึงวันที่</label>
                    <DateInput
                      value={filters.date_to}
                      onChange={(next) =>
                        setFilters((prev) => ({ ...prev, date_to: next }))
                      }
                    />
                  </div>
                  <div className="col-12 col-md-2">
                    <label className="form-label">วิธีจ่าย</label>
                    <select
                      className="form-select"
                      value={filters.payment_method}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          payment_method: e.target.value,
                        }))
                      }
                    >
                      <option value="">ทั้งหมด</option>
                      <option value="CASH">เงินสด</option>
                      <option value="SCAN">แสกน</option>
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label">ค้นหา</label>
                    <input
                      type="text"
                      className="form-control"
                      value={filters.q}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, q: e.target.value }))
                      }
                      placeholder="ค้นหารายการหรือโน้ต"
                    />
                  </div>
                  <div className="col-12">
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setAppliedFilters({ ...filters })}
                        disabled={loading}
                      >
                        ค้นหา
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          const cleared = {
                            date_from: "",
                            date_to: "",
                            payment_method: "",
                            q: "",
                          };
                          setFilters(cleared);
                          setAppliedFilters(cleared);
                        }}
                        disabled={loading}
                      >
                        ล้างตัวกรอง
                      </button>
                    </div>
                  </div>
                </div>

                {/* ====== การ์ดสรุปยอดตามรายการ ====== */}
                <div className="card shadow-sm border-0 rounded-4 mb-3">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="fw-semibold">สรุปยอดตามรายการ</span>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setSummarySelected(itemSummaryRows.map((r) => r.name))}
                        >
                          เลือกทั้งหมด
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setSummarySelected([])}
                        >
                          ล้าง
                        </button>
                      </div>
                    </div>
                    {itemSummaryRows.length === 0 ? (
                      <div className="text-muted small">ไม่มีข้อมูล</div>
                    ) : (
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        {itemSummaryRows.map((row) => {
                          const checked = summarySelected.includes(row.name);
                          return (
                            <button
                              key={row.name}
                              type="button"
                              onClick={() => toggleSummaryItem(row.name)}
                              className={`btn btn-sm rounded-pill ${checked ? "btn-primary" : "btn-outline-secondary"}`}
                            >
                              {row.name}
                              <span className="ms-1 opacity-75">
                                {row.total.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="border-top pt-2 d-flex align-items-baseline gap-2">
                      <span className="text-muted small">ยอดรวมที่เลือก</span>
                      <span className="fs-4 fw-bold text-danger">
                        {summaryTotal.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท
                      </span>
                      {summarySelected.length > 0 && (
                        <span className="text-muted small">({summarySelected.length} รายการ)</span>
                      )}
                    </div>
                    {summarySelected.length > 0 && (
                      <div className="mt-2">
                        <button
                          className={`btn btn-sm ${showMonthlyBreakdown ? "btn-info text-white" : "btn-outline-info"}`}
                          onClick={() => setShowMonthlyBreakdown(p => !p)}
                        >
                          📅 {showMonthlyBreakdown ? "ซ่อนรายเดือน" : "แยกตามเดือน"}
                        </button>
                      </div>
                    )}
                    {showMonthlyBreakdown && monthlyRows.length > 0 && (
                      <div className="mt-2 border rounded bg-white" style={{ maxHeight: 320, overflowY: "auto" }}>
                        {monthlyRows.map(row => {
                          const isOpen = expandedMonths.has(row.month);
                          const rowItems = items.filter(
                            it => summarySelected.includes(it.item_name) && it.expense_date.slice(0, 7) === row.month
                          ).sort((a, b) => b.expense_date.localeCompare(a.expense_date));
                          return (
                            <div key={row.month} className="border-bottom">
                              <button
                                className="w-100 d-flex justify-content-between align-items-center px-2 py-2 fw-semibold small bg-transparent border-0 text-start"
                                style={{ cursor: "pointer" }}
                                onClick={() => toggleExpandMonth(row.month)}
                              >
                                <span>{isOpen ? "▾" : "▸"} {row.label}</span>
                                <span className="text-danger">
                                  {row.total.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท
                                </span>
                              </button>
                              {isOpen && (
                                <div className="px-2 pb-2" style={{ backgroundColor: "#f8f9fa" }}>
                                  {rowItems.map(it => (
                                    <div key={it.id} className="d-flex justify-content-between align-items-center py-1 border-bottom border-light" style={{ fontSize: "0.78rem" }}>
                                      <div className="d-flex gap-2 text-muted">
                                        <span style={{ minWidth: 70 }}>{formatDisplayDate(it.expense_date)}</span>
                                        <span>{it.item_name}</span>
                                        {it.note && <span className="text-secondary fst-italic">({it.note})</span>}
                                      </div>
                                      <span className="text-danger fw-semibold" style={{ whiteSpace: "nowrap" }}>
                                        {it.amount.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {successMsg && <div className="alert alert-success rounded-3">{successMsg}</div>}
                {error && <div className="alert alert-danger rounded-3">{error}</div>}
                <div className="d-flex justify-content-end mb-3">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => void handleDeleteSelected()}
                    disabled={loading || selectedIds.length === 0}
                  >
                    ลบที่เลือก {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
                  </button>
                </div>

                <div className="table-responsive" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                  <table className="table table-hover align-middle">
                    <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ width: 56 }} className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={allSelected}
                            onChange={toggleSelectAll}
                            aria-label="เลือกทั้งหมด"
                          />
                        </th>
                        <th style={{ width: 130 }}>วันที่</th>
                        <th>รายการ</th>
                        <th style={{ width: 140 }} className="text-end">
                          ราคา
                        </th>
                        <th style={{ width: 120 }}>วิธีจ่าย</th>
                        <th>โน้ต</th>
                        <th style={{ width: 150 }} className="text-center">
                          จัดการ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">
                            กำลังโหลดข้อมูล...
                          </td>
                        </tr>
                      )}

                      {!loading && items.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">
                            ไม่พบข้อมูลรายจ่าย
                          </td>
                        </tr>
                      )}

                      {!loading &&
                        items.map((item) => (
                          <tr
                            key={item.id}
                            className={rowToneById.get(item.id) ?? undefined}
                          >
                            <td className="text-center">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedIds.includes(item.id)}
                                onChange={() => toggleSelected(item.id)}
                                aria-label={`เลือกรายการ ${item.item_name}`}
                              />
                            </td>
                            <td>{formatDisplayDate(item.expense_date)}</td>
                            <td className="fw-semibold">{item.item_name}</td>
                            <td className="text-end">
                              {item.amount.toLocaleString("th-TH", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td>{paymentLabel(item.payment_method)}</td>
                            <td className="text-muted">{item.note || "-"}</td>
                            <td className="text-center">
                              <div className="d-inline-flex gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openEdit(item)}
                                >
                                  แก้ไข
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => void handleDelete(item)}
                                >
                                  ลบ
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.45)" }}
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeEdit();
            }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content rounded-4 shadow">
                <div className="modal-header">
                  <h5 className="modal-title">แก้ไขรายจ่าย #{editing.id}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeEdit}
                  />
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <label className="form-label">วันที่</label>
                      <DateInput
                        value={editForm.expense_date}
                        onChange={(next) =>
                          setEditForm((prev) => ({ ...prev, expense_date: next }))
                        }
                      />
                    </div>
                    <div className="col-12 col-md-8">
                      <label className="form-label">รายการ</label>
                      <select
                        className="form-select"
                        value={editForm.item_name}
                        onChange={(e) =>
                          setEditForm((prev) => ({ ...prev, item_name: e.target.value }))
                        }
                        disabled={itemOptionsLoading}
                      >
                        <option value="">เลือกรายการ</option>
                        {editItemOptions.map((option) => (
                          <option key={option.id} value={option.name}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      <div className="form-text">
                        ถ้าต้องการเพิ่มตัวเลือกใหม่ ให้เพิ่มจากฟอร์มหลักด้านซ้าย
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">ราคา</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        step="0.01"
                        value={editForm.amount}
                        onChange={(e) =>
                          setEditForm((prev) => ({ ...prev, amount: e.target.value }))
                        }
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">วิธีจ่าย</label>
                      <select
                        className="form-select"
                        value={editForm.payment_method}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            payment_method: e.target.value as PaymentMethod,
                          }))
                        }
                      >
                        <option value="CASH">เงินสด</option>
                        <option value="SCAN">แสกน</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">โน้ต</label>
                      <textarea
                        className="form-control"
                        rows={4}
                        value={editForm.note}
                        onChange={(e) =>
                          setEditForm((prev) => ({ ...prev, note: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeEdit}
                    disabled={saving}
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void handleUpdate()}
                    disabled={saving}
                  >
                    {saving ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
